import { forwardRef, useContext } from 'react'

import { Input as InputPrimitive } from '@base-ui/react/input'
import { tv } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { FormFieldContext } from '../form-field/form-field-context'

const inputVariants = tv({
  slots: {
    root: [
      'group/input font-body flex w-full min-w-0 items-center gap-2 px-4',
      // Figma's 1px stroke sits outside the box, preserving its height and padding.
      'bg-surface ring-outline ring-1 transition-[background-color,box-shadow]',
      'hover:ring-primary-100',
      'has-[input:focus]:ring-primary',
      'has-[input[placeholder]:focus:not(:placeholder-shown)]:ring-primary-200',
      'has-[input:disabled]:bg-surface-200 has-[input:disabled]:cursor-not-allowed',
      'has-[input:disabled]:ring-outline'
    ],
    input: [
      'w-full min-w-0 flex-1 text-ellipsis border-0 bg-transparent p-0 outline-none',
      'text-surface-title-50 caret-primary',
      'placeholder:text-surface-body font-semibold placeholder:font-normal',
      'group-hover/input:placeholder:font-semibold',
      'focus:placeholder:opacity-0',
      'disabled:font-normal disabled:placeholder:font-normal',
      'disabled:text-surface-body disabled:placeholder:text-surface-body',
      'disabled:cursor-not-allowed'
    ],
    icon: 'flex shrink-0 items-center justify-center'
  },
  variants: {
    size: {
      sm: {
        root: 'h-8 rounded-[8px]',
        input: 'text-[12px] leading-[18px]',
        icon: 'min-h-[18px] min-w-[18px] text-[18px] leading-[18px]'
      },
      md: {
        root: 'h-10 rounded-[10px]',
        input: 'text-[14px] leading-[21px]',
        icon: 'min-h-[21px] min-w-[21px] text-[21px] leading-[21px]'
      },
      lg: {
        root: 'h-12 rounded-[12px]',
        input: 'text-[16px] leading-[24px]',
        icon: 'min-h-6 min-w-6 text-[24px] leading-[24px]'
      }
    },
    error: {
      true: {
        root: [
          'ring-danger-icon',
          'hover:ring-danger-icon',
          'has-[input:focus]:ring-danger-icon',
          'has-[input[placeholder]:focus:not(:placeholder-shown)]:ring-danger-icon',
          'has-[input:disabled]:ring-outline'
        ],
        input: [
          'text-danger-icon caret-danger-icon',
          'placeholder:text-danger-icon',
          'disabled:text-surface-body disabled:placeholder:text-surface-body'
        ]
      }
    }
  },
  compoundVariants: [
    { size: 'sm', error: true, class: { input: 'placeholder:font-semibold' } }
  ],
  defaultVariants: {
    size: 'md',
    error: false
  }
})

export type InputSize = 'sm' | 'md' | 'lg'

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize
  error?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  className?: string
  inputClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      error: errorProp,
      leadingIcon,
      trailingIcon,
      className,
      inputClassName,
      disabled: disabledProp,
      id,
      'aria-invalid': ariaInvalid,
      'aria-required': ariaRequired,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const field = useContext(FormFieldContext)
    const error = errorProp ?? field?.error ?? false
    const disabled = disabledProp ?? field?.disabled
    const styles = inputVariants({ size, error })
    const describedBy =
      [
        ...new Set(
          [ariaDescribedBy, field?.messageId]
            .filter(Boolean)
            .join(' ')
            .split(/\s+/)
        )
      ]
        .filter(Boolean)
        .join(' ') || undefined

    return (
      <div
        data-slot='input-wrapper'
        data-error={error ? '' : undefined}
        data-disabled={disabled ? '' : undefined}
        className={cn(styles.root(), className)}
      >
        {leadingIcon != null && (
          <span
            data-slot='input-leading-icon'
            className={cn(
              styles.icon(),
              error ? 'text-danger-icon' : 'text-surface-title-50',
              'group-has-[input:disabled]/input:text-surface-body'
            )}
          >
            {leadingIcon}
          </span>
        )}
        <InputPrimitive
          ref={ref}
          data-slot='input'
          id={id ?? field?.htmlFor}
          disabled={disabled}
          aria-invalid={ariaInvalid ?? (error || undefined)}
          aria-required={ariaRequired ?? (field?.required || undefined)}
          aria-describedby={describedBy}
          className={cn(styles.input(), inputClassName)}
          {...props}
        />
        {trailingIcon != null && (
          <span
            data-slot='input-trailing-icon'
            className={cn(
              styles.icon(),
              error ? 'text-surface-title-50' : 'text-surface-body',
              size !== 'lg' &&
                'group-has-[input:focus]/input:text-surface-title-50',
              'group-has-[input:disabled]/input:text-surface-body'
            )}
          >
            {trailingIcon}
          </span>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'
